import React, { useState, useEffect } from "react";
import {
  Alert,
  Image,
  ImageBackground,
  Dimensions,
  KeyboardAvoidingView,
  Modal,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import typo from "../../Theme/Typo";
import Colors from "../../Theme/Colors";
import {format} from "date-fns";
import { useTranslation } from "react-i18next";
import { TextInput } from "react-native-paper";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import Entypo from "@expo/vector-icons/Entypo";
import RootStore from "../../Store";
import ImageViewer from "react-native-image-zoom-viewer";
import Button from "../Button";
import * as ImagePicker from "expo-image-picker";
import FontAwesome from "@expo/vector-icons/FontAwesome";
import AntDesign from "@expo/vector-icons/AntDesign";
import { differenceInDays } from "date-fns";
import Queries from "../../api/queries";
import Mutation from "../../api/mutation";

// NEW CAMERA SCREEN
import CameraScreen from "../CameraScreen";
import ModalQLerr from "../../utils/ModalQLerr";
import AlertCustom from "../utils/alert";

const BG_PATH = require("../../../assets/newDesign/background-portrait.png");

const { userInfo } = RootStore.getInstance();

const DATE_DELTA = 3;

const WINDOW_HEIGHT = Dimensions.get("window").height;
const WINDOW_WIDTH = Dimensions.get("window").width;

const EditHistoryEvent = ({ navigation, route }) => {
  const [eventDetail, setEventDetails] = useState({});

  const [QLerror, setQLerror] = useState("");

  const [isLoading, setLoading] = useState<boolean>(false);

  const [validationCommentary, setValidationCommentary] = useState<string>("");
  const [validationPictures, setValidationPictures] = useState<
    { uri: String; base64: String; shouldRemove: Boolean }[]
  >([]);
  const [problems, setProblems] = useState([]);

  const [isViewer, setViewer] = useState({
    isVisible: false,
    index: 0,
  });

  const [MediaAvailable, setMediaAvailable] = useState(false);

  const [CameraOpen, setCameraOpen] = useState(false);

  const queries = new Queries();
  const mutation = new Mutation();

  const { evenDetailParams = {}, eventId } = route.params;

  const { t, i18n } = useTranslation();

  useEffect(() => {
    (async () => {
      // Pictures library permission

      const { granted: mediaGranted = false } =
        await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (mediaGranted) {
        setMediaAvailable(true);
      } else {
        AlertCustom(
          t("TITLE_MSG_PERMISSION_DENIED"),
          t("MSG_PERMISSION_MEDIA_DENIED")
        );
      }
    })();

    setEventDetails(evenDetailParams);
    setProblems(evenDetailParams.problems);
    //console.log(evenDetailParams.problems)
    setPictures(evenDetailParams.validationPicture);
    setValidationCommentary(evenDetailParams.validationPicture.commentary);
  }, [evenDetailParams, eventId]);

  const setPictures = (validationPictures: any) => {
    let pictureArray = [];

    for (let img of validationPictures.image) {
      pictureArray.push({
        uri: img.container + "/" + img.name,
        base64: "",
        shouldRemove: false,
      });
    }

    setValidationPictures(pictureArray);
  };

  const deletePicture = (index) => {
    AlertCustom(
      t("RERMOVE_ACHAT_CONFIRM_TITLE"),
      t("RERMOVE_PICTURE_CONFIRM_BODY"),
      [
        // The "Yes" button
        {
          text: t("RERMOVE_ACHAT_CONFIRM_YES"),
          onPress: () => {
            const temp = [...validationPictures];
            temp[index].shouldRemove = true;
            setValidationPictures(temp);
          },
        },
        // The "No" button
        {
          text: t("RERMOVE_ACHAT_CONFIRM_FALSE"),
        },
      ]
    );
  };

  const addPicture = (picture: object) => {
    const temp = [...validationPictures];
    if(picture?.assets?.[0]) temp.push({
      uri: picture.assets[0].uri,
      base64: picture.assets[0].base64,
      shouldRemove: false,
    });
    setValidationPictures(temp);
  };

  const pickImage = async () => {
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: false,
      base64: true,
      quality: 0.5,
      exif: true,
    });
    // check date taken
    if (
      result &&
      result.assets &&
      result.assets.length > 0 &&
      result.assets[0].exif
    ) {
      if (
        result.assets[0].exif.DateTimeOriginal &&
        result.assets[0].exif.DateTimeOriginal !== ""
      ) {
        const split = result.assets[0].exif.DateTimeOriginal.split(" ");
        let date = split[0].split(":");
        let hour = split[1].split(":");

        // for 20/01/2020 12:00:00 -> date = ['2020', '01', '20'], hour = ['12', '00', '00']
        if (
          differenceInDays(
            new Date(),
            new Date(date[0], date[1] - 1, date[2], hour[0], hour[1], hour[2])
          ) > DATE_DELTA
        ) {
          AlertCustom(
            t("VALIDATION_PICTURE_OUT_OF_DATE"),
            t("VALIDATION_PICTURE_OUT_OF_DATE_CONTENT")
          );
          setLoading(false);
          return;
        }
      }
    }
    if (!result.canceled) {
      setLoading(true);
      addPicture(result);
    }
    setLoading(false);
  };

  const getEventDetails = async () => {
    setLoading(true);
    try {
      const data = await queries.getEventDetail(eventId, i18n.language);

      let errors = data?.errors;

      if (errors && errors.length > 0) {
        const errorMessage: string = errors[0].message;

        setQLerror(errorMessage);
      } else {
        const newEventDetail = data.data.getDetailOfEvent.eventDetail;
        setProblems(newEventDetail.problems);
      }
    } catch (err) {
      console.log("graphql error", JSON.stringify(err, null, 2));
      setQLerror(err);
    } finally {
      setLoading(false);
    }
  };

  const editFinishedEvent = async () => {
    let authorized = false;

    const editObject = {
      validationPicture: validationPictures,
      commentary: validationCommentary,
    };

    for (let pic of validationPictures) {
      if (pic.base64 !== "" || !pic.shouldRemove) {
        authorized = true;
        break;
      }
    }

    try {
      if (authorized) {
        const edition = await mutation.editFinishedEvent(
          eventId,
          editObject,
          userInfo.getToken()
        );
        if (edition.data?.editFinishedEvent?.result === 1) {
          AlertCustom(
            t("VALIDATION_PICTURE_SUCCESS"),
            t("VALIDATION_PICTURE_SUCCESS_BODY")
          );
          navigation.navigate("AgendaScreen", { navigation: navigation });
        } else {
          console.log(edition.errors);
        }
      } else {
        AlertCustom(
          t("VALIDATION_PICTURE_MISSING"),
          t("VALIDATION_PICTURE_MISSING_BODY")
        );
        navigation.goBack();
      }
    } catch (err) {
      console.log(JSON.stringify(err, null, 2));
      console.warn(err);
    }
  };

  const helpIncidentInfo = () => {
    AlertCustom(t("EVENT_PROBLEM_TITLE"), t("HELP_INCIDENT_INFO"));
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      style={style.containerKeyboard}
    >
      <SafeAreaView style={typo.mainBackground}>
        <ImageBackground source={BG_PATH} style={typo.background}>
          <ScrollView>
            <Modal
              visible={CameraOpen}
              onRequestClose={() => setCameraOpen(false)}
            >
              <CameraScreen
                closeCamera={() => setCameraOpen(false)}
                isChat={false}
                pictures={validationPictures}
                setPictures={setValidationPictures}
              />
            </Modal>

            {QLerror && QLerror !== "" ? (
              <ModalQLerr reason={QLerror} resetErr={() => setQLerror("")} />
            ) : null}
            <View style={typo.container}>
              <Text style={typo.titleInfo}>{t("EVENT_TITLE")}</Text>
              <View>
                <View style={typo.resCard}>
                  <View style={typo.underlineCat}>
                    <MaterialIcons
                      name="info"
                      size={25}
                      style={{ marginRight: 10 }}
                      color={Colors.oiqia}
                    />
                    <Text style={typo.category}>{t("EVENT_STATUS_TITLE")}</Text>
                  </View>
                  <View>
                    {eventDetail.type === "cleaner" && (
                      <View>
                        <Text style={[typo.h1, style.newTitle]}>
                          <MaterialIcons
                            style={{ marginRight: 10 }}
                            name="cleaning-services"
                            size={20}
                            color={Colors.oiqia}
                          ></MaterialIcons>{" "}
                          {eventDetail.title}
                        </Text>
                      </View>
                    )}
                    {eventDetail.type === "maintainer" && (
                      <View>
                        <Text style={[typo.h1, style.newTitle]}>
                          <MaterialIcons
                            style={{ marginRight: 10 }}
                            name="build"
                            size={20}
                            color={Colors.oiqia}
                          ></MaterialIcons>{" "}
                          {eventDetail.title}
                        </Text>
                      </View>
                    )}
                    {eventDetail.type === "other" && (
                      <View>
                        <Text style={[typo.h1, style.newTitle]}>
                          <MaterialIcons
                            style={{ marginRight: 10 }}
                            name="flag"
                            size={20}
                            color={Colors.oiqia}
                          ></MaterialIcons>{" "}
                          {eventDetail.title}
                        </Text>
                      </View>
                    )}
                    <View style={{ marginTop: 3 }}>
                      <Text style={typo.body}>
                        <MaterialIcons
                          name="location-pin"
                          size={18}
                          color={Colors.black}
                        ></MaterialIcons>
                        {eventDetail.nameOfAppart}
                      </Text>
                    </View>
                  </View>
                  {eventDetail.description != "" && (
                    <View style={{ marginTop: 5, marginBottom: 5 }}>
                      <Text style={style.newDesc}>
                        {eventDetail.description}
                      </Text>
                    </View>
                  )}
                  <View style={style.columnNew}>
                    <View style={style.left}>
                      <MaterialIcons
                        name="calendar-today"
                        color={Colors.oiqia}
                        size={20}
                      ></MaterialIcons>
                    </View>
                    <View style={style.right}>
                      <Text style={style.newDate}>
                        {eventDetail.start && eventDetail.end && (
                          <Text>
                            {format(
                              new Date(eventDetail?.start),
                              "dd/MM/yyyy "
                            )}
                            {format(new Date(eventDetail?.start), "HH:mm")} →{" "}
                            {format(new Date(eventDetail?.end), "HH:mm")}
                          </Text>
                        )}
                      </Text>
                    </View>
                  </View>
                  {eventDetail.statusOfEvent &&
                    eventDetail?.statusOfEvent[
                      eventDetail?.statusOfEvent.length - 1
                    ].value === "Évènement confirmé" && (
                      <View style={style.columnNew}>
                        <View style={style.left}>
                          <MaterialIcons
                            name="event-available"
                            size={20}
                            color={Colors.green}
                          />
                        </View>
                        <View style={style.right}>
                          <Text style={{ color: Colors.green }}>
                            {
                              eventDetail?.statusOfEvent[
                                eventDetail?.statusOfEvent.length - 1
                              ].value
                            }
                          </Text>
                        </View>
                      </View>
                    )}
                  {eventDetail.statusOfEvent &&
                    eventDetail?.statusOfEvent[
                      eventDetail?.statusOfEvent.length - 1
                    ].value === "Créé" && (
                      <View style={style.columnNew}>
                        <View style={style.left}>
                          <MaterialIcons
                            name="create"
                            size={20}
                            color={Colors.grey}
                          />
                        </View>
                        <View style={style.right}>
                          <Text style={{ color: Colors.grey }}>
                            {
                              eventDetail?.statusOfEvent[
                                eventDetail?.statusOfEvent.length - 1
                              ].value
                            }
                          </Text>
                        </View>
                      </View>
                    )}
                  {eventDetail.statusOfEvent &&
                    eventDetail?.statusOfEvent[
                      eventDetail?.statusOfEvent.length - 1
                    ].value === "Incident déclaré" && (
                      <View style={style.columnNew}>
                        <View style={style.left}>
                          <MaterialIcons
                            name="report-problem"
                            size={20}
                            color={Colors.red}
                          />
                        </View>
                        <View style={style.right}>
                          <Text style={{ color: Colors.red }}>
                            {
                              eventDetail?.statusOfEvent[
                                eventDetail?.statusOfEvent.length - 1
                              ].value
                            }
                          </Text>
                        </View>
                      </View>
                    )}
                  {eventDetail.statusOfEvent &&
                    eventDetail?.statusOfEvent[
                      eventDetail?.statusOfEvent.length - 1
                    ].value === "Fini" && (
                      <View style={style.columnNew}>
                        <View style={style.left}>
                          <MaterialIcons
                            name="check-circle-outline"
                            size={20}
                            color={Colors.green}
                          />
                        </View>
                        <View style={style.right}>
                          <Text style={{ color: Colors.green }}>
                            {
                              eventDetail?.statusOfEvent[
                                eventDetail?.statusOfEvent.length - 1
                              ].value
                            }
                          </Text>
                        </View>
                      </View>
                    )}
                  {eventDetail.statusOfEvent &&
                    eventDetail?.statusOfEvent[
                      eventDetail?.statusOfEvent.length - 1
                    ].value === "Annulé" && (
                      <View style={style.columnNew}>
                        <View style={style.left}>
                          <MaterialIcons
                            name="cancel"
                            size={24}
                            color={Colors.red}
                          />
                        </View>
                        <View style={style.right}>
                          <Text style={{ color: Colors.red }}>
                            {
                              eventDetail?.statusOfEvent[
                                eventDetail?.statusOfEvent.length - 1
                              ].value
                            }
                          </Text>
                        </View>
                      </View>
                    )}
                </View>

                <View style={typo.resCard}>
                  <View style={typo.underlineCat}>
                    <MaterialIcons
                      name="report-problem"
                      size={24}
                      style={{ marginRight: 10 }}
                      color={Colors.oiqia}
                      onPress={() => helpIncidentInfo()}
                    />
                    <Text style={typo.category}>
                      {t("EVENT_PROBLEM_TITLE")}
                    </Text>
                  </View>
                  {problems && problems.title != "" ? (
                    <View>
                      <TextInput
                        activeOutlineColor={Colors.oiqia}
                        value={problems.title}
                        label={t("PROBLEM_TITLE_LABEL")}
                        style={{
                          marginTop: 10,
                          marginBottom: 10,
                          backgroundColor: "white",
                        }}
                        mode="outlined"
                        disabled
                      />
                      <TextInput
                        activeOutlineColor={Colors.oiqia}
                        value={problems.description}
                        label={t("PROBLEM_DESCRIPTION_LABEL")}
                        style={{
                          marginTop: 10,
                          marginBottom: 10,
                          backgroundColor: "white",
                        }}
                        mode="outlined"
                        disabled
                      />
                      <View style={typo.catCard}>
                        <ScrollView horizontal>
                          {problems &&
                          problems.image &&
                          problems.image.length > 0 ? (
                            problems.image.map((element, index) => (
                              <View style={{ margin: 5 }} key={index}>
                                <Image
                                  style={style.image}
                                  source={{ uri: element }}
                                />
                              </View>
                            ))
                          ) : (
                            <View style={typo.catCard}>
                              <Text
                                style={[typo.body, { alignSelf: "center" }]}
                              >
                                {t("PROBLEM_PICTURES_EMPTY")}
                              </Text>
                            </View>
                          )}
                        </ScrollView>
                      </View>
                    </View>
                  ) : (
                    <View style={[typo.catCard, { width: "100%" }]}>
                      <Text style={[typo.body, { alignSelf: "center" }]}>
                        {t("EVENT_PROBLEM_EMPTY")}
                      </Text>
                    </View>
                  )}
                  {problems && problems.title == "" ? (
                    <Button
                      text={t("EVENT_PROBLEM_BUTTON")}
                      style={{
                        alignSelf: "center",
                        backgroundColor: Colors.oiqia,
                        marginTop: 10,
                      }}
                      onPress={() =>
                        navigation.navigate("AddProblem", {
                          eventId,
                          refreshPicture: getEventDetails,
                        })
                      }
                    />
                  ) : (
                    <Button
                      text="Modier l'incident"
                      style={{
                        alignSelf: "center",
                        backgroundColor: Colors.oiqia,
                        marginTop: 10,
                      }}
                      onPress={() =>
                        navigation.navigate("AddProblem", {
                          eventId,
                          problem: problems,
                          refreshPicture: getEventDetails,
                        })
                      }
                    />
                  )}
                </View>

                <View style={[typo.resCard, { marginBottom: 80 }]}>
                  <View style={typo.underlineCat}>
                    <MaterialIcons
                      name="info"
                      size={24}
                      style={{ marginRight: 10 }}
                      color={Colors.oiqia}
                    />
                    <Text style={typo.category}>
                      {t("EVENT_FINISH_PRESTA")}
                    </Text>
                  </View>
                  <Text style={[typo.body, { marginBottom: 20, marginTop: 5 }]}>
                    {t("EVENT_FINISH_DETAILS")}
                  </Text>
                  <View style={typo.catCard}>
                    {validationPictures.length > 0 ? (
                      <ScrollView horizontal>
                        {validationPictures.map((element, index) => {
                          if (!element.shouldRemove) {
                            return (
                              <View key={index} style={{ margin: 5 }}>
                                <TouchableOpacity
                                  onPress={() =>
                                    setViewer({ isVisible: true, index })
                                  }
                                >
                                  <Image
                                    style={style.image}
                                    source={{ uri: element.uri }}
                                  />
                                </TouchableOpacity>
                                <View style={{ alignSelf: "center" }}>
                                  <FontAwesome
                                    name="trash"
                                    size={24}
                                    color={Colors.red}
                                    onPress={() => deletePicture(index)}
                                  />
                                </View>
                              </View>
                            );
                          }
                        })}
                      </ScrollView>
                    ) : (
                      <View style={{ alignSelf: "center" }}>
                        <Text style={typo.body}>
                          {t("PROBLEM_PICTURES_EMPTY")}
                        </Text>
                      </View>
                    )}
                  </View>
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-around",
                      marginTop: 10,
                      alignItems: "center",
                    }}
                  >
                    <TouchableOpacity
                      style={style.colImg}
                      onPress={() => setCameraOpen(true)}
                    >
                      <AntDesign name="camera" size={30} color={Colors.oiqia} />
                      <Text>{t("PROBLEM_PICTURE_TAKE")}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={style.colImg}
                      disabled={!MediaAvailable}
                      onPress={() => pickImage()}
                    >
                      <AntDesign
                        name="picture"
                        size={30}
                        color={MediaAvailable ? Colors.oiqia : Colors.black}
                      />
                      <Text>{t("PROBLEM_PICTURE_PICK")}</Text>
                    </TouchableOpacity>
                  </View>
                  <View>
                    <TextInput
                      activeOutlineColor={Colors.oiqia}
                      value={validationCommentary}
                      label={t("EVENT_FINISH_PICTURE_COMMENTARY")}
                      style={{
                        marginTop: 10,
                        marginBottom: 10,
                        backgroundColor: "white",
                      }}
                      mode="outlined"
                      onChangeText={setValidationCommentary}
                      multiline
                      /* error={descriptionError} */
                    />
                  </View>
                  {Platform.OS != "web" && validationPictures.length > 0 && (
                    <SafeAreaView style={{ flex: 1 }}>
                      <Modal visible={isViewer.isVisible} transparent={true}>
                        <ImageViewer
                          key={isViewer.index}
                          enableSwipeDown={true}
                          index={isViewer.index}
                          imageUrls={validationPictures.map((ele) => {
                            return {
                              url: ele.uri,
                              props: { source: ele.uri },
                            };
                          })}
                          renderHeader={() => (
                            <SafeAreaView>
                              <TouchableOpacity
                                onPress={() =>
                                  setViewer({ isVisible: false, index: 0 })
                                }
                              >
                                <Entypo
                                  name="cross"
                                  onPress={() =>
                                    setViewer({ isVisible: false, index: 0 })
                                  }
                                  size={60}
                                  color="white"
                                />
                              </TouchableOpacity>
                            </SafeAreaView>
                          )}
                        ></ImageViewer>
                      </Modal>
                    </SafeAreaView>
                  )}
                  <Button
                    text={t("EVENT_EDIT_FINISH_BUTTON")}
                    style={{
                      alignSelf: "center",
                      backgroundColor:
                        validationPictures && validationPictures.length > 0
                          ? Colors.oiqia
                          : Colors.grey,
                      marginTop: 10,
                    }}
                    onPress={() => editFinishedEvent()}
                    disabled={
                      validationPictures && validationPictures.length < 1
                    }
                  />
                </View>
              </View>
            </View>
          </ScrollView>
        </ImageBackground>
      </SafeAreaView>
    </KeyboardAvoidingView>
  );
};

const style = StyleSheet.create({
  containerKeyboard: {
    flex: 1,
  },
  newDate: {
    fontWeight: "bold",
    color: Colors.oiqia,
    textAlignVertical: "center",
  },
  colImg: {
    alignItems: "center",
    marginBottom: 15,
    marginTop: 10,
  },
  newDesc: {
    color: Colors.black,
    fontStyle: "italic",
    fontSize: 18,
  },
  newTitle: {
    color: Colors.oiqia,
  },
  columnNew: {
    flex: 1,
    alignSelf: "flex-start",
    flexDirection: "row",
    marginTop: 10,
  },
  left: {
    alignSelf: "flex-start",
  },
  right: {
    marginLeft: 5,
    alignSelf: "flex-end",
  },
  center: {
    flex: 1,
    justifyContent: "flex-end",
  },
  titleWelcome: {
    fontSize: 18,
    color: "black",
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "RobotoMedium",
  },
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  column: {
    flex: 1,
    alignSelf: "auto",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  dateInfo: {
    width: "50%",
    padding: 5,
    textAlign: "left",

    justifyContent: "flex-start",
  },
  underline: {
    borderColor: "#0A0A0AE7",
    borderBottomWidth: 0.5,
    paddingBottom: 5,
    marginBottom: 10,
  },
  item: {
    marginTop: 10,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  textCheckBox: {
    maxWidth: 320,
  },
  image: {
    left: 5,
    top: 5,
    width: 200,
    height: 150,
    borderWidth: 1,
    borderRadius: 1,
    marginRight: 10,
    borderColor: "white",
    marginBottom: 5,
  },
  cautionMessage: {
    color: "red",
  },
});

export default EditHistoryEvent;
